/**
 * TeachPopup Component
 * Popup menu that appears when user selects text in the PDF.
 * Allows user to "teach" the system what the selected text represents.
 */

import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  Button,
  ButtonGroup,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Chip,
  Divider,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  School as TeachIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';
import { RuleScope } from '../../types/bom.types';

interface TeachPopupProps {
  selectedText: string;
  position: { x: number; y: number };
  onClose: () => void;
  onTeach: (text: string, componentType: string, scope: RuleScope) => Promise<void>;
  isLoading?: boolean;
  success?: boolean;
}

// Common component types for quick selection
const QUICK_TYPES = [
  { value: 'MCB', label: 'Circuit Breaker', icon: '⚡' },
  { value: 'CONTACTOR', label: 'Contactor', icon: '🔌' },
  { value: 'RELAY', label: 'Relay', icon: '🔀' },
  { value: 'TERMINAL', label: 'Terminal', icon: '📍' },
  { value: 'PLC', label: 'PLC', icon: '🖥️' },
  { value: 'MOTOR', label: 'Motor', icon: '⚙️' },
  { value: 'FUSE', label: 'Fuse', icon: '🔧' },
  { value: 'FAN', label: 'Fan', icon: '🌀' },
  { value: 'GENERATOR', label: 'Generator', icon: '🔋' },
  { value: 'HMI', label: 'HMI', icon: '📺' },
  { value: 'POWER_SUPPLY', label: 'Power Supply', icon: '🔌' },
  { value: 'SWITCH', label: 'Switch', icon: '🎚️' },
  { value: 'TRANSFORMER', label: 'Transformer', icon: '⚡' },
  { value: 'SENSOR', label: 'Sensor', icon: '📡' },
];

const ALL_TYPES = [
  ...QUICK_TYPES,
  { value: 'IO_MODULE', label: 'I/O Module', icon: '📊' },
  { value: 'FILTER', label: 'Filter', icon: '🔍' },
  { value: 'HEATER', label: 'Heater', icon: '🔥' },
  { value: 'SOCKET', label: 'Socket', icon: '🔗' },
  { value: 'CABLE', label: 'Cable', icon: '📶' },
  { value: 'DIN_RAIL', label: 'DIN Rail', icon: '📏' },
  { value: 'BUSBAR', label: 'Busbar', icon: '➖' },
  { value: 'ENCLOSURE', label: 'Enclosure', icon: '📦' },
  { value: 'MARKER', label: 'Marker', icon: '🏷️' },
  { value: 'OTHER', label: 'Other', icon: '❓' },
];

const TeachPopup: React.FC<TeachPopupProps> = ({
  selectedText,
  position,
  onClose,
  onTeach,
  isLoading = false,
  success = false,
}) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [customType, setCustomType] = useState('');
  const [scope, setScope] = useState<RuleScope>('global');
  const [selectedType, setSelectedType] = useState<string | null>(null);

  const handleQuickTeach = async (type: string) => {
    setSelectedType(type);
    await onTeach(selectedText, type, scope);
  };

  const handleCustomTeach = async () => {
    if (customType.trim()) {
      setSelectedType(customType.toUpperCase());
      await onTeach(selectedText, customType.toUpperCase(), scope);
    }
  };

  // Success state
  if (success) {
    return (
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          left: Math.min(position.x, window.innerWidth - 320),
          top: Math.min(position.y + 10, window.innerHeight - 100),
          zIndex: 9999,
          p: 2,
          maxWidth: 300,
          backgroundColor: '#e8f5e9',
          border: '2px solid #4caf50',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SuccessIcon sx={{ color: '#4caf50' }} />
          <Typography variant="body1" fontWeight="bold" color="#2e7d32">
            Pattern Learned!
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mt: 1, color: '#1b5e20' }}>
          "{selectedText}" → {selectedType}
        </Typography>
        <Button
          size="small"
          onClick={onClose}
          sx={{ mt: 1 }}
          fullWidth
          variant="outlined"
          color="success"
        >
          Done
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        left: Math.min(position.x, window.innerWidth - 380),
        top: Math.min(position.y + 10, window.innerHeight - 400),
        zIndex: 9999,
        p: 2,
        maxWidth: 360,
        maxHeight: '80vh',
        overflow: 'auto',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TeachIcon color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight="bold">
            Teach System
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Selected Text */}
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          backgroundColor: 'action.hover',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block">
          Selected Text:
        </Typography>
        <Typography
          variant="body1"
          fontWeight="bold"
          sx={{
            fontFamily: 'monospace',
            wordBreak: 'break-all',
          }}
        >
          "{selectedText}"
        </Typography>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Quick Type Selection */}
      {!isLoading && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            What is this? (Quick Select)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
            {QUICK_TYPES.slice(0, 8).map((type) => (
              <Chip
                key={type.value}
                label={`${type.icon} ${type.value}`}
                size="small"
                onClick={() => handleQuickTeach(type.value)}
                clickable
                variant="outlined"
                sx={{
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'primary.contrastText',
                  },
                }}
              />
            ))}
          </Box>

          {/* Advanced Options Toggle */}
          <Button
            size="small"
            onClick={() => setShowAdvanced(!showAdvanced)}
            endIcon={showAdvanced ? <CollapseIcon /> : <ExpandIcon />}
            sx={{ mb: 1 }}
          >
            {showAdvanced ? 'Less Options' : 'More Options'}
          </Button>

          <Collapse in={showAdvanced}>
            <Divider sx={{ my: 1 }} />

            {/* More Component Types */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              All Component Types:
            </Typography>
            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
              <InputLabel>Select Type</InputLabel>
              <Select
                label="Select Type"
                value={customType || ''}
                onChange={(e) => setCustomType(e.target.value)}
              >
                {ALL_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Custom Type Input */}
            <TextField
              fullWidth
              size="small"
              label="Or enter custom type"
              value={customType}
              onChange={(e) => setCustomType(e.target.value.toUpperCase())}
              placeholder="e.g., GENERATOR"
              sx={{ mb: 2 }}
            />

            {/* Scope Selection */}
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
              Apply this rule to:
            </Typography>
            <ButtonGroup fullWidth size="small" sx={{ mb: 2 }}>
              <Button
                variant={scope === 'global' ? 'contained' : 'outlined'}
                onClick={() => setScope('global')}
              >
                🌍 Global
              </Button>
              <Button
                variant={scope === 'project' ? 'contained' : 'outlined'}
                onClick={() => setScope('project')}
              >
                📁 Project
              </Button>
              <Button
                variant={scope === 'file' ? 'contained' : 'outlined'}
                onClick={() => setScope('file')}
              >
                📄 File Only
              </Button>
            </ButtonGroup>

            {/* Apply Custom Button */}
            <Button
              fullWidth
              variant="contained"
              onClick={handleCustomTeach}
              disabled={!customType.trim()}
              startIcon={<TeachIcon />}
            >
              Apply Custom Rule
            </Button>
          </Collapse>

          {/* Cancel Link */}
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button size="small" color="inherit" onClick={onClose}>
              Cancel
            </Button>
          </Box>
        </>
      )}
    </Paper>
  );
};

export default TeachPopup;








